import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Alert,
  Chip,
  TextField,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface MarkCompletedUnassignedSSCCDialogProps {
  open: boolean;
  ssccId: string;
  ssccStatus: string;
  actionReason: string;
  isProcessing: boolean;
  onClose: () => void;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
}

export default function MarkCompletedUnassignedSSCCDialog({
  open,
  ssccId,
  ssccStatus,
  actionReason,
  isProcessing,
  onClose,
  onReasonChange,
  onConfirm
}: MarkCompletedUnassignedSSCCDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        }
      }}>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
          color: '#fff',
          px: 3,
          py: 3
        }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              backdropFilter: 'blur(10px)'
            }}>
            <CheckCircleIcon sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#fff' }}>
              Mark SSCC as Completed
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', mt: 0.5 }}>
              Manually complete this SSCC
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3, '& > :first-of-type': { mt: 1 } }}>
        <Stack spacing={3}>
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(2, 136, 209, 0.2)',
              backgroundColor: 'rgba(2, 136, 209, 0.04)',
              fontSize: '0.875rem',
              '& .MuiAlert-icon': {
                color: '#0288d1'
              }
            }}>
            Use this action when process or system issues prevent standard completion of the SSCC. All ASNs will be
            filled in maximum quantity and receipted.
          </Alert>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              backgroundColor: 'rgba(76, 175, 80, 0.04)',
              border: '2px solid rgba(76, 175, 80, 0.15)'
            }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#4caf50'
                  }}>
                  SSCC ID
                </Typography>
                <Typography variant="h6" fontWeight={600} sx={{ mt: 1, color: '#388e3c' }}>
                  {ssccId || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#4caf50'
                  }}>
                  Current Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={ssccStatus}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'text.secondary'
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                fontSize: '0.875rem'
              }}>
              Reason for Completion{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              required
              value={actionReason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Explain why this SSCC needs to be manually marked as completed (e.g., system error, process issue, etc.)..."
              helperText="This reason will be logged in the audit trail"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          gap: 1.5
        }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            color: 'text.secondary',
            fontSize: '0.875rem',
            px: 2.5,
            py: 1
          }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={!actionReason.trim() || isProcessing}
          startIcon={
            isProcessing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 18 }} />
          }
          sx={{
            px: 3,
            py: 1,
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            backgroundColor: '#4caf50',
            '&:hover': { backgroundColor: '#388e3c' },
            boxShadow: 'none',
            borderRadius: 1.5
          }}>
          {isProcessing ? 'Processing...' : 'Mark SSCC as Completed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
